import React from 'react';
import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material';
import { TrialStatus } from '../../actions/subscription/getTrialStatus';

interface TrialStatusBannerProps {
  trial: TrialStatus;
}

/**
 * Shown above the pricing tiers. Three variants:
 *   - trialing: "Your 14-day free trial is active — X days left, $Y of $15 used"
 *   - trial_expired: red urgency banner; user must subscribe to continue
 *   - usage_limit hit during trial: warning that trial spend is at the cap
 *
 * Returns null for paid / past_due / canceled / free states (component should
 * not render at all in those cases — the caller decides).
 */
const TrialStatusBanner: React.FC<TrialStatusBannerProps> = ({ trial }) => {
  if (trial.status === 'trial_expired') {
    return (
      <Alert
        severity="error"
        data-testid="trial-banner-expired"
        sx={{ mb: 3, textAlign: 'left' }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Your 14-day free trial has ended.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Choose a plan below to keep using AI Scribe.
        </Typography>
      </Alert>
    );
  }

  if (trial.status !== 'trialing') return null;

  const cap = trial.capUsd ?? 15;
  const spent = trial.spentUsd;
  const percentSpent = cap > 0 ? Math.min(100, (spent / cap) * 100) : 0;
  const daysLeft = trial.daysLeft ?? 0;
  const hitCap = trial.blocked && trial.blockedReason === 'usage_limit';

  return (
    <Alert
      severity={hitCap ? 'warning' : 'info'}
      data-testid="trial-banner-active"
      icon={hitCap ? undefined : false}
      sx={{
        mb: 3,
        textAlign: 'left',
        borderColor: 'primary.main',
      }}
    >
      <Stack spacing={1}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {hitCap
            ? "You've used the full $15 of trial credit."
            : `🎉 Your 14-day free trial is active — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left.`}
        </Typography>
        <Box>
          <LinearProgress
            variant="determinate"
            value={percentSpent}
            color={hitCap ? 'error' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            ${spent.toFixed(2)} of ${cap.toFixed(2)} used
          </Typography>
        </Box>
        {hitCap && (
          <Typography variant="body2">
            Subscribe to a plan below to keep using AI Scribe.
          </Typography>
        )}
      </Stack>
    </Alert>
  );
};

export default TrialStatusBanner;
